import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { debounce } from '../../platform/utils/commonUtils';


interface WindowSize {
  width: number;
  height: number;
}

export function useWindowSize(): WindowSize {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);

  }, []); // Empty array ensures that effect runs only on mount and unmount


  return windowSize;
}


export function useDebouncedWindowSize(delay: number = 100): WindowSize {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const clearDebounceRef = useRef<() => void>(() => null);

  useEffect(() => {
    const [debouncedHandleResize, clearDebounce] = debounce(() => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, delay);

    function handleResize() {
      debouncedHandleResize();
    }

    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    clearDebounceRef.current = clearDebounce;

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearDebounceRef.current();
    };

  }, [delay]); // Include delay in dependencies to recreate the debounced function if delay changes

  return windowSize;
}


export function useElementWidth(ref: React.RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    setWidth(el.getBoundingClientRect().width);

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}
